import { type FieldValues, type UseFormReturn, useForm, FormProvider, type DefaultValues } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import type { z } from "zod"
import { Button } from "@/components/ui/button"
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Spinner } from "components"
import { useEffect } from "react"
import { cn } from "@/lib/utils" // Assuming you have a cn utility function

interface ModalFormProps<T extends FieldValues> {
  title: string
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: T) => void
  schema?: z.ZodType<T>
  defaultValues?: DefaultValues<T>;
  children: (form: UseFormReturn<T>) => React.ReactNode;
  submitButtonText?: string;
  isLoading?: boolean;
  skipDirtyCheck?: boolean;
  // NEW: Dynamic class name props
  className?: string; // For AlertDialogContent (modal container)
  contentClassName?: string; // For the scrollable content area
}

export function ModalForm<T extends FieldValues>({
  title,
  isOpen,
  onClose,
  onSubmit,
  schema,
  defaultValues,
  children,
  submitButtonText = "Save",
  isLoading = false,
  skipDirtyCheck = false,
  className,
  contentClassName
}: ModalFormProps<T>) {
  const form = useForm<T>({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  })

  const handleSubmit = async (values: T) => {   
    onSubmit(values)    
    form.reset()
  }

  const handleClose = () => {
    onClose()
    form.reset()
  }

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
  }, [defaultValues])

  const hasValues = () => {
    if (skipDirtyCheck) return true;
    
    const values = form.getValues();
    if (Array.isArray(values.invoices)) {
      return values.invoices.some((invoice: any) => invoice.invoice_file);
    }
    return form.formState.isDirty;
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogPortal>
        {isLoading && <Spinner/>}
        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />        
        <AlertDialogContent 
          className={cn(
            "sm:max-w-[500px] p-0 rounded-lg gap-1 bg-white-A700 animate-contentShow",
            className
          )}
        >
          <AlertDialogHeader className="flex text-left gap-4 p-4 border-b">
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="">
            <FormProvider {...form}>
              <form className="" onSubmit={e=>{
                e.preventDefault()
                form.handleSubmit(handleSubmit)()
              }
              }>
                <div 
                  className={cn(
                    "space-y-4 h-full px-4 overflow-auto max-h-[700px]",
                    contentClassName
                  )}
                >
                  {children(form)}
                </div>
                <AlertDialogFooter className="flex gap-2 items-center flex-row p-4">
                  <Button 
                    className="text-blue-700 border border-blue-700 w-[100px]" 
                    type="button" 
                    variant="outline" 
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button 
                    disabled={form.formState.isSubmitting || !hasValues()} 
                    className="bg-blue-700 border border-blue-700 text-white-A700 w-[100px]" 
                    type="submit"
                  >
                    {submitButtonText}
                  </Button>
                </AlertDialogFooter>
              </form>
            </FormProvider>
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  )
}